import { graphql, Link } from "gatsby"
import React from "react"
import MarkdownContent from "components/MarkdownContent"
import { SEOHead } from "components/SEO"
import SectionHeader from "components/Layout/SectionHeader"
import Container from "components/ui/Container"
import Layout from "components/ui/Layout"

function JobTemplate({ data, location: { pathname } }) {
  const { markdownRemark: job } = data
  const { frontmatter, htmlAst, html } = job

  return (
    <Layout
      pageContentType="job-posting"
      pageName={frontmatter.slug}
      pagePath={pathname}
    >
      <Container section>
        <article className="pt-24 lg:pt-32">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Link
              to="/careers/"
              className="text-cove hover:text-azure font-medium"
            >
              ← Back to All Jobs
            </Link>
          </div>

          {/* Job Header */}
          <header className="mb-12">
            <div className="mb-4">
              <span className="inline-block px-3 py-1 text-sm font-medium text-azure bg-sage rounded-full">
                {frontmatter.department}
              </span>
            </div>
            <SectionHeader title={frontmatter.title} level="h1" />
            <div className="mt-4 flex flex-wrap gap-4 text-gray-600">
              <span className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {frontmatter.location}
              </span>
              <span className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {frontmatter.type}
              </span>
            </div>
          </header>

          {/* Job Content */}
          <div className="max-w-3xl">
            <MarkdownContent
              className="prose prose-lg prose-dark-grey"
              htmlAst={htmlAst}
              fallbackHtml={html}
            />
          </div>

          {/* Apply CTA */}
          <div className="mt-16 p-8 bg-gray-50 rounded-lg">
            <h2 className="text-2xl font-bold text-big-stone mb-4">
              Interested in this role?
            </h2>
            <p className="text-gray-600 mb-6">
              We&apos;d love to hear from you. Send us your resume and tell us
              why you&apos;d be a great fit for the team.
            </p>
            <a
              href={`mailto:careers@preset.io?subject=Application: ${frontmatter.title}`}
              className="inline-block px-6 py-3 bg-lush !text-white font-medium rounded-full hover:bg-gossamer transition-colors"
            >
              Apply Now
            </a>
          </div>
        </article>
      </Container>
    </Layout>
  )
}

export function Head({ data }) {
  const { markdownRemark: job } = data
  const { frontmatter } = job

  return (
    <SEOHead
      title={`${frontmatter.title} - Careers`}
      description={`Join Preset as a ${frontmatter.title}. ${frontmatter.location} - ${frontmatter.type}.`}
    />
  )
}

export default JobTemplate

export const pageQuery = graphql`
  query JobPostingQuery($id: String!) {
    markdownRemark(id: { eq: $id }) {
      htmlAst
      html
      frontmatter {
        title
        department
        location
        type
        slug
      }
    }
  }
`
